import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import classNames from 'classnames';
import Cookies from 'js-cookie';
import PropTypes from 'prop-types';
import React, { useEffect, useCallback, useState } from 'react';
import { PatternFormat } from 'react-number-format';
import { useMediaQuery } from 'react-responsive';

import Button, { TYPES as BUTTON_TYPES } from 'components/Button';
import Image from 'components/ImageV2';
import Input from 'components/Input';
import Modal from 'components/Modal';
import { IMAGE_WIDTHS } from 'constants/images';
import { largeUp } from 'constants/mediaQueries';
import { SIGNUP_MODAL_COOKIES } from 'state/constants';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';
import Validator from 'utils/Validator';

export const TYPES = {
  EMAIL: 'Email',
  SMS: 'SMS',
  SUCCESS: 'Success',
};

export const SIGNUP_TYPES = {
  [TYPES.EMAIL]: {
    ARIA_LABEL: I18n.localize('app.emailAddress'),
    COMPONENT: IncentiveStep,
    PLACEHOLDER: I18n.localize('emailCapture.enterYourEmail'),
    TEST_INPUT_ID: 'email',
    TEST_SUBMIT_ID: 'email-submit',
    VALIDATION_ERROR_MESSAGE: I18n.localize('emailCapture.emailValidationErrorMessage'),
  },
  [TYPES.SMS]: {
    ARIA_LABEL: I18n.localize('app.phoneNumber'),
    COMPONENT: IncentiveStep,
    PLACEHOLDER: I18n.localize('emailCapture.enterYourMobileNumber'),
    TEST_INPUT_ID: 'sms',
    TEST_SUBMIT_ID: 'sms-submit',
    VALIDATION_ERROR_MESSAGE: I18n.localize('emailCapture.phoneValidationErrorMessage'),
  },
  [TYPES.SUCCESS]: {
    COMPONENT: SuccessStep,
  },
};

export default function SignupModal({
  backgroundColor,
  image,
  isOpen,
  onClose,
  onOpen,
  onSubmit,
  modalSignUpPurpose,
  showIncentivizedSignupModal,
  steps,
}) {
  const [emailAddress, setEmailAddress] = useState('');
  const { currentStep, nextStep, setCurrentStep } = useStep(0);

  const handleOnClose = useCallback(() => {
    setEmailAddress('');
    setCurrentStep(0);
    onClose();
  }, [onClose, setCurrentStep]);

  useEffect(() => {
    if (showIncentivizedSignupModal) {
      onOpen();
      Cookies.set(SIGNUP_MODAL_COOKIES.SIGNUP_MODAL, Date.now());
    }
  }, [onOpen, showIncentivizedSignupModal]);

  const isLargeUp = useMediaQuery({ query: largeUp });

  if (showIncentivizedSignupModal === undefined || steps.length === 0) return null;

  const { type } = steps[currentStep];
  const StepComponent = SIGNUP_TYPES[type].COMPONENT;
  const imageWidth = isLargeUp ? IMAGE_WIDTHS.W_1600 : IMAGE_WIDTHS.W_1000;

  return (
    <Modal
      className="SignupModal__modal"
      isOpen={isOpen}
      onRequestClose={handleOnClose}
      portalClassName="SignupModal__portal"
    >
      <div className="SignupModal">
        <div className="SignupModal__image">
          <Image src={image.file.url} width={imageWidth} coverParentNode />
        </div>

        <div className="SignupModal__content">
          <div aria-live="polite" className="screen-reader-only">
            Step {currentStep + 1} of {steps.length}
          </div>

          <StepComponent
            {...steps[currentStep]}
            currentStep={currentStep}
            emailAddress={emailAddress}
            key={type}
            modalSignUpPurpose={modalSignUpPurpose}
            onNextStep={nextStep}
            onSubmit={onSubmit}
            setEmailAddress={setEmailAddress}
            type={type}
          />
        </div>

        <style jsx>{`
          .SignupModal {
            background-color: ${backgroundColor};
            display: grid;
            font-size: ${Theme.get('font-size-mobile-px')};
            grid-template: 33% 1fr / 1fr;
            height: 600px;
            line-height: 1.5;
            padding: 0;
            text-align: center;
            width: 330px;
          }
          .SignupModal__content {
            display: grid;
            gap: 16px;
            padding: 16px;
            place-self: center;
          }

          @media ${largeUp} {
            .SignupModal {
              font-size: 1em;
              grid-template: auto / 41.75% 1fr;
              height: 500px;
              width: 855px;
            }
            .SignupModal__content {
              padding: 32px;
            }
          }
        `}</style>

        <style jsx global>{`
          .SignupModal__modal.ReactModal__Content {
            min-width: 0;
            padding: 0;
          }
          .SignupModal__modal p {
            margin-bottom: 0;
          }
          .SignupModal__modal .SignupModal__input {
            background-color: ${Theme.get('colors-white')};
          }
          .SignupModal__modal .SignupModal__sms-input {
            width: 100%;
            appearance: none;
            -webkit-appearance: none;
            outline: none;
            height: 48px;
            font-size: ${Theme.get('font-size-px')};
            letter-spacing: 0.8px;
            font-weight: ${Theme.get('font-weight-bold')};
            background: ${Theme.get('colors-white')};
            border-bottom-color: ${Theme.get('colors-black')};
            border-width: 0;
            border-bottom-width: 2px;
            border-style: solid;
            border-radius: 0;
            padding: 2px ${Theme.get('spacing') * 2}px 0;
            color: ${Theme.get('colors-black')};
            box-shadow:
              0 2px 0 0 inset transparent,
              2px 0 0 0 inset transparent,
              -2px 0 0 0 inset transparent;
            transition:
              border-color ${Theme.get('transition-duration-ms')}
                ${Theme.get('transition-timing-ease-out-quad')},
              background-color ${Theme.get('transition-duration-ms')}
                ${Theme.get('transition-timing-ease-out-quad')},
              box-shadow ${Theme.get('transition-duration-ms')}
                ${Theme.get('transition-timing-ease-out-quad')};
            -webkit-tap-highlight-color: transparent;
          }
          .SignupModal__sms-input::placeholder {
            color: ${Theme.get('colors-black-40')};
          }

          .SignupModal__sms-input:focus:not(.SignupModal__sms-input--invalid),
          .SignupModal__sms-input:active:not(.SignupModal__sms-input--invalid) {
            box-shadow:
              0 2px 0 0 inset ${Theme.get('colors-black')},
              2px 0 0 0 inset ${Theme.get('colors-black')},
              -2px 0 0 0 inset ${Theme.get('colors-black')};
          }

          .SignupModal .SignupModal__sms-input--invalid {
            border-color: ${Theme.get('colors-error')};
            box-shadow:
              0 2px 0 0 inset ${Theme.get('colors-error')},
              2px 0 0 0 inset ${Theme.get('colors-error')},
              -2px 0 0 0 inset ${Theme.get('colors-error')};
          }
        `}</style>
      </div>
    </Modal>
  );
}

SignupModal.propTypes = {
  backgroundColor: PropTypes.string,
  id: PropTypes.string,
  image: PropTypes.shape({
    file: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
  isOpen: PropTypes.bool.isRequired,
  modalSignUpPurpose: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  showIncentivizedSignupModal: PropTypes.bool,
  steps: PropTypes.array,
};

SignupModal.defaultProps = {
  backgroundColor: Theme.get('colors-neutral-20'),
  id: undefined,
  image: {
    file: {
      url: '',
    },
  },
  isOpen: false,
  showIncentivizedSignupModal: undefined,
  modalSignUpPurpose: '',
  steps: [],
};

function useStep(initialStep) {
  const [currentStep, setCurrentStep] = useState(initialStep);

  const nextStep = useCallback(() => {
    setCurrentStep(prev => prev + 1);
  }, []);

  return {
    currentStep,
    nextStep,
    setCurrentStep,
  };
}

export const validateInput = (type, value) => {
  switch (type) {
    case TYPES.EMAIL:
      return Validator.isEmail(value);
    case TYPES.SMS:
      return Validator.isPhone(value);
    default:
      return false;
  }
};

function useSignupModal({
  discountPercentage,
  emailAddress,
  onNextStep,
  onSubmit,
  modalSignUpPurpose,
  setEmailAddress,
  type,
}) {
  const [value, setValue] = useState('');
  const [hasErrors, setHasErrors] = useState(null);

  const handleBlur = useCallback(() => {
    setHasErrors(null);
  }, []);

  const handleChange = useCallback(
    event => {
      setValue(event.target.value);
      setEmailAddress(event.target.value);
      if (hasErrors) {
        setHasErrors(null);
      }
    },
    [hasErrors, setEmailAddress]
  );

  const handlePhoneChange = useCallback(
    ({ value: phoneValue }) => {
      setValue(phoneValue);
      if (hasErrors) {
        setHasErrors(null);
      }
    },
    [hasErrors]
  );

  function handleSubmit(event) {
    event.preventDefault();
    const isInputValid = validateInput(type, value);
    const hasErrors = isInputValid === false;

    if (hasErrors) {
      setHasErrors(hasErrors ?? null);
      return;
    }

    onSubmit({
      emailAddress,
      origination: 'signupModal',
      purpose: modalSignUpPurpose,
      acceptsMarketing: true,
      incentiveType: type,
      incentiveDiscount: String(discountPercentage),
      ...(type === TYPES.SMS ? { phone: value } : {}),
    });
    setHasErrors(null);
    onNextStep();
  }

  return {
    handleBlur,
    handleSubmit,
    handleChange,
    handlePhoneChange,
    hasErrors,
    value,
  };
}

function IncentiveStep({
  cta,
  discount: { discountPercentage },
  type,
  disclaimer,
  emailAddress,
  setEmailAddress,
  heading,
  modalSignUpPurpose,
  onNextStep,
  onSubmit,
  skippable,
  subtext,
}) {
  const { handleSubmit, handleBlur, handleChange, handlePhoneChange, hasErrors, value } =
    useSignupModal({
      discountPercentage,
      onNextStep,
      onSubmit,
      modalSignUpPurpose,
      type,
      emailAddress,
      setEmailAddress,
    });

  return (
    <>
      <p className="Typography--secondary-heading fade-in">{heading}</p>
      <div className="fade-in animation-delay-1">{documentToReactComponents(subtext)}</div>
      <form className="SignupModal__form fade-in animation-delay-2" onSubmit={handleSubmit}>
        {type === TYPES.EMAIL && (
          <Input
            name={type}
            ariaLabel={SIGNUP_TYPES[type].ARIA_LABEL}
            className="SignupModal__input"
            data-testid={SIGNUP_TYPES[type].TEST_INPUT_ID}
            isValid={hasErrors ? false : null}
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder={SIGNUP_TYPES[type].PLACEHOLDER}
          />
        )}
        {type === TYPES.SMS && (
          <PatternFormat
            name={type}
            aria-label={SIGNUP_TYPES[type].ARIA_LABEL}
            className={classNames('SignupModal__sms-input', {
              'SignupModal__sms-input--invalid': hasErrors,
            })}
            data-testid={SIGNUP_TYPES[type].TEST_INPUT_ID}
            format="(###) ###-####"
            onBlur={handleBlur}
            onValueChange={handlePhoneChange}
            placeholder={SIGNUP_TYPES[type].PLACEHOLDER}
            value={value}
          />
        )}
        <div
          className={classNames('SignupModal__errors', {
            'SignupModal__errors--visible': hasErrors,
          })}
        >
          <p className="SignupModal__error-text">{SIGNUP_TYPES[type].VALIDATION_ERROR_MESSAGE}</p>
        </div>
        <Button isSubmit isFullWidth data-testid={SIGNUP_TYPES[type].TEST_SUBMIT_ID}>
          {cta}
        </Button>
      </form>

      {skippable && (
        <Button
          className="fade-in animation-delay-3"
          type={BUTTON_TYPES.OUTLINE}
          isFullWidth
          data-testid="skip-button"
          onClick={() => {
            onNextStep();
          }}
        >
          {I18n.localize('emailCapture.skip')}
        </Button>
      )}

      <div className="SignUpModal__disclaimer fade-in animation-delay-4">
        {documentToReactComponents(disclaimer)}
      </div>

      <style jsx>{`
        .SignupModal__form {
          display: grid;
          text-align: left;
          gap: ${Theme.get('spacing') * 2}px;
        }
        .SignUpModal__disclaimer {
          font-size: ${Theme.get('font-size-description-px')};
          line-height: 1.25;
        }
        .SignupModal__errors {
          color: ${Theme.get('colors-error')};
          overflow: hidden;
          max-height: 0;
          transition: max-height ${Theme.get('transition-duration-ms')} ease-in-out;
        }
        .SignupModal__errors--visible {
          max-height: 18px;
        }
        .SignupModal__error-text {
          font-size: ${Theme.get('font-size-small-px')};
          font-weight: ${Theme.get('font-weight-semi-bold')};
          line-height: 1;
        }

        @media ${largeUp} {
          .SignupModal__form {
            gap: 12px;
          }
          .SignupModal__error-text {
            font-size: ${Theme.get('font-size-description-px')};
          }
        }
      `}</style>

      <style jsx global>{`
        .SignUpModal__disclaimer a {
          font-weight: ${Theme.get('font-weight-semi-bold')};
        }
      `}</style>
    </>
  );
}

IncentiveStep.propTypes = {
  cta: PropTypes.string,
  disclaimer: PropTypes.object,
  discount: PropTypes.shape({
    discountPercentage: PropTypes.number,
  }),
  emailAddress: PropTypes.string.isRequired,
  setEmailAddress: PropTypes.func.isRequired,
  heading: PropTypes.string,
  modalSignUpPurpose: PropTypes.string,
  onNextStep: PropTypes.func,
  onSubmit: PropTypes.func,
  skippable: PropTypes.bool,
  subtext: PropTypes.object,
  type: PropTypes.string,
};

function SuccessStep({ heading, subtext }) {
  return (
    <>
      <p className="Typography--secondary-heading fade-in animation-delay-1">{heading}</p>
      <div className="fade-in animation-delay-2">{documentToReactComponents(subtext)}</div>
    </>
  );
}

SuccessStep.propTypes = {
  heading: PropTypes.string,
  subtext: PropTypes.object,
};

SuccessStep.defaultProps = {
  heading: '',
  subtext: {},
};
